import { InternalAccessor, ItemsPerPage, Table, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'react-table'

import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { productColumnIdSortPropertyMap } from '@features/products/constants/productColumnIdSortPropertyMap'
import { ProductSortProperty } from '@features/products/enums/productSortProperty'
import { useProductsListFilters } from '@features/products/hooks/useProductsListFilters'
import { Product } from '@features/products/types/product'
import { VatRateItemValue } from '@features/vatRates/elements/VatRateSelect/types/vatRateItemValue'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../enums/modalId'
import { SortDirection } from '../../../../enums/sortDirection'
import { useLocale } from '../../../../hooks/useLocale'
import { Currency } from '../../../../types/currency'
import { isLanguage, Language } from '../../../../types/language'
import { LocaleIdentifier } from '../../../../types/localeIdentifier'
import { getTableColumnIdDirection } from '../../../../utils/getTableColumnIdDirection'
import { languageToLocale } from '../../../../utils/languageToLocale'
import { DeleteProductsConfirmationModal } from './elements/DeleteProductsConfirmationModal'
import { ProductsTableHeader } from './elements/ProductsTableHeader'
import { ProductsTableHoverActionHandler } from './elements/ProductsTableHoverActions/types/productsTableHoverActionHandler'
import { UpdateVATConfirmationModal } from './elements/UpdateProductsVatConfirmationModal'
import { useProductsListColumns } from './hooks/useProductsListColumns'

interface ProductsListProps {
  currentPage?: number
  isError?: boolean
  isLoading: boolean
  onDuplicateProductClick?: ProductsTableHoverActionHandler
  onEditProductClick?: ProductsTableHoverActionHandler
  pageSize?: ItemsPerPage | 'all'
  products: Product[]
  searchQuery?: string
  totalPageItems: number
}

export const ProductsList = ({
  isLoading,
  onDuplicateProductClick,
  onEditProductClick,
  products,
  totalPageItems,
  isError = false,
}: ProductsListProps): ReactElement => {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([])
  const [isWorkingOnRowAction, setIsWorkingOnRowAction] = useState(false)
  const [selectedVatRate, setSelectedVatRate] = useState<VatRateItemValue>()

  const { t } = useTranslation()

  const { code } = useLocale()
  const { organization } = useUserOrganization()
  const [{ pageSize, page, searchQuery, sortProperty, sortDirection }, setFilters] = useProductsListFilters()

  const { open: openDeleteProductsConfirmationModal } = useModal(ModalId.DeleteProductsConfirmationModal)

  const language: Language = isLanguage(code) ? code : 'en'
  const locale: LocaleIdentifier | undefined = languageToLocale[language]
  const currency: Currency = organization?.baseCurrencyId
    ? (organization?.baseCurrencyId as Currency)
    : DEFAULT_CURRENCY
  const withPagination = pageSize !== 'all' && selectedProducts.length === 0
  const emptyRowInfo = isError ? t('products.table.could_not_load_products') : undefined
  const itemsPerPage = pageSize === 'all' ? undefined : pageSize

  const handleDeleteProductClick = useCallback<ProductsTableHoverActionHandler>(
    (product: Product) => {
      setIsWorkingOnRowAction(true)
      setSelectedProducts([product])
      openDeleteProductsConfirmationModal()
    },
    [openDeleteProductsConfirmationModal],
  )

  const { columns } = useProductsListColumns({
    currency,
    locale,
    handleDeleteProductClick,
    onDuplicateProductClick,
    onEditProductClick,
  })

  const sortedColumnId = useMemo(
    () =>
      getTableColumnIdDirection<ProductSortProperty | undefined>(sortProperty, sortDirection === SortDirection.Desc),
    [sortDirection, sortProperty],
  )

  const data = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        [InternalAccessor.HoverActions]: true,
      })),
    [products],
  )

  const handleRowSelect = useCallback((row: Row<Product>, isSelected: boolean) => {
    setSelectedProducts((selectedProducts) =>
      isSelected
        ? [...selectedProducts, row.original]
        : selectedProducts.filter((product) => product.id !== row.original.id),
    )
  }, [])

  const handleAllRowsSelect = useCallback((rows: Row<Product>[], isSelected: boolean) => {
    setSelectedProducts(isSelected ? rows.map((row) => row.original) : [])
  }, [])

  const handleDeleteProductsSuccess = useCallback(() => {
    setIsWorkingOnRowAction(false)
    setSelectedProducts([])
  }, [])

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setFilters({ pageSize: itemsPerPage, page: 1 })
    },
    [setFilters],
  )

  const handlePageChange = useCallback((page: number) => setFilters({ page }), [setFilters])

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setFilters({
        sortProperty: productColumnIdSortPropertyMap?.[columnId],
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setFilters],
  )

  const handleDeleteProductsConfirmationModalCancel = useCallback(() => {
    if (isWorkingOnRowAction) {
      setIsWorkingOnRowAction(false)
      setSelectedProducts([])
    }
  }, [isWorkingOnRowAction])

  const handleDeleteProductsConfirmationModalError = useCallback(() => {
    if (isWorkingOnRowAction) {
      setIsWorkingOnRowAction(false)
      setSelectedProducts([])
    }
  }, [isWorkingOnRowAction])

  const handleRowClick = useCallback(
    ({ original }: Row<Product>) => onEditProductClick?.(original),
    [onEditProductClick],
  )

  const handleUpdateProductsVatSuccess = useCallback(() => {
    setSelectedVatRate(undefined)
  }, [])

  const handleVatRateSelect = useCallback((vatRate?: VatRateItemValue) => {
    setSelectedVatRate(vatRate)
  }, [])

  const handleUpdateProductsVatRateCancel = useCallback(() => {
    setSelectedVatRate(undefined)
  }, [])

  const handleSearchChange = useCallback(
    (searchQuery: string) =>
      setFilters({
        page: 1,
        searchQuery: searchQuery === '' ? undefined : searchQuery,
      }),
    [setFilters],
  )

  return (
    <>
      <ProductsTableHeader
        onVatRateSelect={handleVatRateSelect}
        onSearchChange={handleSearchChange}
        productCount={totalPageItems}
        searchQuery={searchQuery}
        selectedProducts={isWorkingOnRowAction ? [] : selectedProducts}
        selectedVatRate={selectedVatRate}
      />
      <Table
        columns={columns}
        currentPage={page}
        data={data}
        emptyRowInfo={emptyRowInfo}
        isLoading={isLoading}
        isSelectable
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        onRowSelect={handleRowSelect}
        onSort={handleSort}
        onAllRowsSelect={handleAllRowsSelect}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortedColumnId={sortedColumnId}
        totalPageItems={totalPageItems}
        withItemsPerPageSelect
        withPagination={withPagination}
        withSkeletonView
        withStickyHeader
      />
      <DeleteProductsConfirmationModal
        onCancel={handleDeleteProductsConfirmationModalCancel}
        onDeleteProductsError={handleDeleteProductsConfirmationModalError}
        onDeleteProductsSuccess={handleDeleteProductsSuccess}
        selectedProducts={selectedProducts}
      />
      <UpdateVATConfirmationModal
        onUpdateProductsVatRateCancel={handleUpdateProductsVatRateCancel}
        onUpdateProductsVatRateSuccess={handleUpdateProductsVatSuccess}
        selectedProducts={selectedProducts}
        selectedVatRate={selectedVatRate}
      />
    </>
  )
}
